/**
 * SpCredentialReset — inline pre-fill-form control to view + set/reset the
 * dedicated Service Principal's Epic sign-in credentials (epic-sp-login-email +
 * epic-sp-login) for mortgagee_update_v1_2 SP mode.
 *
 * These ride the existing per-user credential API (/credentials/status +
 * /credentials/update) — the SAME transport as the standing "Flow passwords"
 * panel (FlowPasswordManager), which also surfaces them now that they're in the
 * substrate's _KNOWN_USER_CREDENTIALS allowlist. This component is the
 * convenience reset ON the launch form, scoped to the two SP creds, and reports
 * readiness up so the launcher can block a launch that would otherwise stop
 * immediately with `sp_credentials_missing`.
 *
 * Add-in copy; ora mirrors this (ui/CLAUDE.md parity).
 *
 * features/3-plans/mortgagee-v1-sp-lock.md.
 */
import { useCallback, useEffect, useState } from "react";
import ErrorPanel from "./ErrorPanel";
import { orchestratorApi } from "../services/orchestratorApi";

const SP_EMAIL_CRED = "epic-sp-login-email";
const SP_PASSWORD_CRED = "epic-sp-login";

/**
 * Fetch whether BOTH dedicated-SP credentials are set for the current user.
 * Exported so the drawer can run the readiness check at drawer level — the
 * Epic sign-in disclosure is collapsed by default, and this component only
 * mounts when it's expanded, so the launch gate can't rely on the component's
 * own fetch. Errors report not-ready (fail closed, matching the launch gate).
 */
export async function fetchSpCredsReady(): Promise<boolean> {
  try {
    const statuses = await orchestratorApi.getMyCredentialStatus();
    const pwd = statuses.find((s) => s.credential_name === SP_PASSWORD_CRED);
    const mail = statuses.find((s) => s.credential_name === SP_EMAIL_CRED);
    return pwd?.status === "set" && mail?.status === "set";
  } catch {
    return false;
  }
}

export interface SpCredentialResetProps {
  /** Fires whenever readiness changes — true only when BOTH SP creds are 'set'. */
  onReadyChange?: (ready: boolean) => void;
  disabled?: boolean;
}

export function SpCredentialReset({ onReadyChange, disabled }: SpCredentialResetProps) {
  // null = still loading; true/false = both-set readiness.
  const [ready, setReady] = useState<boolean | null>(null);
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    const isReady = await fetchSpCredsReady();
    setReady(isReady);
    onReadyChange?.(isReady);
  }, [onReadyChange]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSave = useCallback(async () => {
    if (!email.trim() || !password) {
      setError(new Error("Enter both the SP sign-in email and password."));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Email first, then password — both are per-user credentials keyed by name.
      await orchestratorApi.updateMyFlowPassword(SP_EMAIL_CRED, email.trim());
      await orchestratorApi.updateMyFlowPassword(SP_PASSWORD_CRED, password);
      setPassword("");
      setEditing(false);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSaving(false);
    }
  }, [email, password, refresh]);

  const statusChip =
    ready === null ? (
      <span className="inline-flex items-center rounded-full border border-primary-gray-light-300 bg-primary-gray-light-100 px-2 py-[2px] text-2xs font-semibold text-primary-gray-light-600">
        Checking…
      </span>
    ) : ready ? (
      <span
        className="inline-flex items-center rounded-full border border-primary-success-200 bg-primary-success-50 px-2 py-[2px] text-2xs font-semibold text-primary-success-700"
        data-testid="sp-creds-status-set"
      >
        Set
      </span>
    ) : (
      <span
        className="inline-flex items-center rounded-full border border-primary-warning-300 bg-primary-warning-50 px-2 py-[2px] text-2xs font-semibold text-primary-warning-700"
        data-testid="sp-creds-status-not-set"
      >
        Not set
      </span>
    );

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-primary-gray-light-200 bg-base-white p-3"
      data-testid="sp-credential-reset"
    >
      <div className="flex flex-row items-center justify-between gap-2">
        <span className="text-sm font-semibold text-primary-gray-light-900">
          Service Principal credentials
        </span>
        {statusChip}
      </div>

      {ready === false && !editing && (
        <p className="text-xs text-primary-warning-700" data-testid="sp-creds-not-set-prompt">
          Set your Service Principal sign-in email and password before launching —
          otherwise the run will stop immediately.
        </p>
      )}

      {editing ? (
        <div className="flex flex-col gap-2">
          {error && (
            <ErrorPanel
              error={error}
              title="Couldn't save Service Principal credentials"
              description="Your credentials weren't saved. You can try again."
              compact
            />
          )}
          <input
            type="email"
            placeholder="SP sign-in email"
            aria-label="Service Principal sign-in email"
            className="rounded border border-primary-gray-light-300 px-2 py-1 text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={saving}
            data-testid="sp-creds-email"
          />
          <input
            type="password"
            placeholder="SP password"
            aria-label="Service Principal password"
            className="rounded border border-primary-gray-light-300 px-2 py-1 text-sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={saving}
            data-testid="sp-creds-password"
          />
          <div className="flex flex-row items-center gap-2">
            <button
              type="button"
              className="btn-secondary-gray px-3 py-1 text-xs"
              onClick={() => void handleSave()}
              disabled={saving}
              data-testid="sp-creds-save"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="text-xs text-primary-gray-light-600 hover:text-primary-gray-light-900 disabled:opacity-50"
              onClick={() => {
                setEditing(false);
                setError(null);
                setPassword("");
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn-secondary-gray self-start px-3 py-1 text-xs"
          onClick={() => {
            setError(null);
            setEditing(true);
          }}
          disabled={disabled}
          data-testid="sp-creds-edit"
        >
          {ready ? "Reset SP email / password" : "Set SP email / password"}
        </button>
      )}
    </div>
  );
}

export default SpCredentialReset;
